import type { HexMapState } from '../../../engine/render/renderer';
import { t } from '../../../i18n/translations';
import { overlayTokens } from '../styles/tokens';
import type { DismissibleGroupVM, DismissibleItemVM, ProgressVM } from './types';
import type { TextMeasurer } from './buildActionBarVM';
import {
    buildAlertDismissibles,
    buildNotificationDismissibles,
    getDismissibleDetail,
    getDismissibleProgress,
    getDismissibleTitle,
} from './dismissibles';

const {
    dismissible: dismissibleLayout,
    alert: alertLayout,
    dismissButton,
    dismissAction,
} = overlayTokens.layout;

export function buildDismissibles(
    state: HexMapState,
    measureText: TextMeasurer,
    startY: number,
): { notifications: DismissibleGroupVM | null; alerts: DismissibleGroupVM | null; endY: number } {
    if (!state.overlay.showNotifications) {
        return { notifications: null, alerts: null, endY: startY };
    }
    const lang = state.config.ui.language;
    const notifications = buildNotificationDismissibles(state, lang).filter(
        (notification) => !state.dismissedNotifications.has(notification.id),
    );

    let cursorY = startY;
    let notificationGroup: DismissibleGroupVM | null = null;

    if (notifications.length > 0) {
        const headerRect = {
            x: dismissibleLayout.x,
            y: cursorY,
            w: dismissibleLayout.width,
            h: dismissibleLayout.headerHeight,
        };
        cursorY += dismissibleLayout.headerHeight + 8;
        const items = notifications.map((note) => {
            const rect = {
                x: dismissibleLayout.x,
                y: cursorY,
                w: dismissibleLayout.width,
                h: dismissibleLayout.itemHeight,
            };
            const dismissRect = {
                x: rect.x + rect.w - dismissButton.insetX,
                y: rect.y + dismissButton.insetY,
                w: dismissButton.w,
                h: dismissButton.h,
            };
            const progressData = getDismissibleProgress(note);
            const progressVM = progressData
                ? toProgressVM(progressData.current, progressData.total)
                : undefined;
            const action = note.action();
            const actionRect = action
                ? {
                      x: rect.x + dismissAction.insetX,
                      y: rect.y + dismissAction.insetY,
                      w: dismissAction.w,
                      h: dismissAction.h,
                  }
                : undefined;
            const item: DismissibleItemVM = {
                id: note.dismissActionId(),
                kind: 'notification',
                severity: 'info',
                rect,
                dismissRect,
                action: action
                    ? {
                          id: action.id,
                          label: action.label,
                          rect: actionRect!,
                          enabled: true,
                      }
                    : undefined,
                title: getDismissibleTitle(note, lang),
                detail: getDismissibleDetail(note, lang),
                detailLines: [],
                progress: progressVM,
            };
            cursorY += dismissibleLayout.itemHeight + dismissibleLayout.gap;
            return item;
        });
        notificationGroup = {
            kind: 'notification',
            header: { rect: headerRect, title: t(lang, 'overlay.noticeTitle') },
            items,
        };
    }

    const alerts = buildAlertDismissibles(state);
    const alertStart = cursorY + 10;
    let alertGroup: DismissibleGroupVM | null = null;

    if (alerts.length > 0) {
        let alertY = alertStart;
        const headerRect = {
            x: dismissibleLayout.x,
            y: alertY,
            w: alertLayout.width,
            h: alertLayout.headerHeight,
        };
        alertY += alertLayout.headerHeight + 8;
        const items = alerts.map((alert) => {
            const rect = {
                x: dismissibleLayout.x,
                y: alertY,
                w: alertLayout.width,
                h: alertLayout.itemHeight,
            };
            const dismissRect = {
                x: rect.x + rect.w - dismissButton.insetX,
                y: rect.y + dismissButton.insetY,
                w: dismissButton.w,
                h: dismissButton.h,
            };
            const detail = getDismissibleDetail(alert, lang);
            const detailLines = wrapText(
                measureText,
                detail,
                overlayTokens.fonts.small,
                rect.w - 44,
            );
            const item: DismissibleItemVM = {
                id: alert.dismissActionId(),
                kind: 'alert',
                severity: alert.severity,
                rect,
                dismissRect,
                title: '',
                detail,
                detailLines,
            };
            alertY += alertLayout.itemHeight + alertLayout.gap;
            return item;
        });

        alertGroup = {
            kind: 'alert',
            header: { rect: headerRect, title: t(lang, 'overlay.alertTitle') },
            items,
        };
    }

    const endY = alertGroup
        ? alertGroup.items[alertGroup.items.length - 1].rect.y + alertLayout.itemHeight
        : cursorY;

    return { notifications: notificationGroup, alerts: alertGroup, endY };
}

function wrapText(
    measureText: TextMeasurer,
    text: string,
    font: string,
    maxWidth: number,
): string[] {
    const words = text.split(' ');
    const lines: string[] = [];
    let current = '';
    for (const word of words) {
        const test = current ? `${current} ${word}` : word;
        if (measureText(test, font) <= maxWidth) {
            current = test;
        } else {
            if (current) lines.push(current);
            current = word;
        }
    }
    if (current) lines.push(current);
    return lines;
}

function toProgressVM(current: number, total: number): ProgressVM {
    if (total <= 0) {
        return { current, total, ratio: 0 };
    }
    const ratio = Math.min(1, Math.max(0, current / total));
    return { current, total, ratio };
}
